/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { formatDate } from 'uplisting-frontend/helpers/date-format';
import { DisputeStatus } from 'uplisting-frontend/models/dispute';
import {
  ProtectionState,
  chipClass,
  disputeState,
} from 'uplisting-frontend/utils/protection-status';
import type { ModelRegistry } from 'ember-data/types/registries/model';

interface IArgs {
  disputes: ModelRegistry['dispute'][];

  onSelect: (dispute: ModelRegistry['dispute']) => void;
}

interface UiProtectionDisputesTableSignature {
  Element: HTMLDivElement;

  Args: IArgs;
}

export interface IDisputeRow {
  dispute: ModelRegistry['dispute'];
  guestName?: string;
  property?: string;
  statusKey: string;
  statusClass: string;
  needsYou: boolean;
  amount: number;
  currency: string;
  whenKey?: string;
  whenUrgent?: boolean;
  daysLeft?: number;
  whenDate?: string;
}

interface IWhen {
  whenKey?: string;
  whenUrgent?: boolean;
  daysLeft?: number;
  whenDate?: string;
}

function whenKey(key: string): string {
  return `protection_disputes.table.when.${key}`;
}

export default class UiProtectionDisputesTableComponent extends Component<UiProtectionDisputesTableSignature> {
  @cached
  get rows(): IDisputeRow[] {
    return this.args.disputes.map((dispute) => this.toRow(dispute));
  }

  @cached
  get count(): number {
    return this.rows.length;
  }

  private toRow(dispute: ModelRegistry['dispute']): IDisputeRow {
    const state = disputeState(dispute.status);

    return {
      dispute,
      guestName: dispute.booking?.guestName,
      property: dispute.booking?.property?.nickname,
      statusKey: `dispute_status.${dispute.status}`,
      statusClass: chipClass(state),
      needsYou: state === ProtectionState.needs,
      amount: dispute.amount,
      currency: dispute.currency,
      ...this.when(dispute),
    };
  }

  private when(dispute: ModelRegistry['dispute']): IWhen {
    if (dispute.status === DisputeStatus.needsResponse) {
      return this.deadline(dispute.respondBy);
    }

    if (dispute.status === DisputeStatus.underReview) {
      return this.on('sent_on', dispute.lastEventAt);
    }

    return this.on('decided_on', dispute.closedAt ?? dispute.lastEventAt);
  }

  private deadline(respondBy: Date | undefined): IWhen {
    if (!respondBy) {
      return {};
    }

    const days = differenceInCalendarDays(respondBy, new Date());

    if (days > 0) {
      return { whenKey: whenKey('due_in'), whenUrgent: true, daysLeft: days };
    }

    return {
      whenKey: whenKey(days === 0 ? 'due_today' : 'overdue'),
      whenUrgent: true,
    };
  }

  private on(key: string, date: Date | undefined): IWhen {
    if (!date) {
      return {};
    }

    return { whenKey: whenKey(key), whenDate: formatDate(date, 'd LLL yyyy') };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Protection::DisputesTable': typeof UiProtectionDisputesTableComponent;
    'ui/protection/disputes-table': typeof UiProtectionDisputesTableComponent;
  }
}
